/* eslint-disable ember/no-side-effects, ember/no-computed-properties-in-native-classes */
import { computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismMetricBlock } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import changeColorClass from 'lumen/helpers/change-color-class';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import hasFeature from 'lumen/helpers/has-feature';
import { Occupancy } from 'lumen/models';
import { FIXED_EXTRACT_DATE_LATEST } from 'lumen/utils/constants/market-insight-constants';
import otbOutOfSync from 'lumen/utils/otb-out-of-sync';

interface HotelMarketInsightCardsOccupancySignature {
  Args: {
    currentExtractDate: unknown;
    refDaysAgo: unknown;
    selectedDayModel: unknown;
  };
}

export default class HotelMarketInsightCardsOccupancy extends Component<HotelMarketInsightCardsOccupancySignature> {
  @service declare access: Services['access'];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  previousTemporaryOccupancy = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLatestCurrentExtractDate() {
    return this.args.currentExtractDate === FIXED_EXTRACT_DATE_LATEST;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketOtbChangeValue() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.occupancyDataForCurrentExtractDate.benchmarkChangeVersusPercentage(
      this.referenceOccupancyChange,
    );
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.currentExtractDate',
    'args.selectedDayModel.occupancy.occupancyChanges',
    'previousTemporaryOccupancy',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get occupancyDataForCurrentExtractDate() {
    if (this.args.currentExtractDate === FIXED_EXTRACT_DATE_LATEST) {
      // Dealing with latest - we can just return the occupancy data for the selected day
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.selectedDayModel.occupancy;
    }

    // The annoying thing is that occupancyChanges are just a property on the occupancy model.
    // We don't have all the transformed data / CPs of a occupancy model object here :(
    // - API refactor wasn't possible at the moment, so we can't easily change occupancychanges to a separate model.
    // This code tries to transform the occupancychange data so that the OTB chart can handle it without extra checks
    const occupancyChangesData = {
      roomsBooked: null,
      roomsBookedIndividual: null,
      roomsBookedGroup: null,
      totalCapacity: null,
      occupancyPercentage: null,
      formattedOccupancyPercentage: this.intl.t('general.missing'),
      formattedBenchmarkPercentage: this.intl.t('general.missing'),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      occupancyChangeForExtractDate: () => null,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      occupancyChangeVersusPercentage: () => null,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      roomsBookedChangeVersus: () => null,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      benchmarkChangeVersusPercentage: () => null,
    };

    // occupancyChanges can be undefined (if occupancy is missing) OR currentExtractDate can still be null
    if (
      // @ts-expect-error: Incorrect type
      !this.args.selectedDayModel.occupancy ||
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !this.args.selectedDayModel.occupancy.occupancyChanges ||
      !this.args.currentExtractDate
    ) {
      return occupancyChangesData;
    }

    if (this.previousTemporaryOccupancy) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.previousTemporaryOccupancy.unloadRecord();
    }

    // Not latest -> we need to search the OTB data in the occupancychanges
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const occupancyChangeForCurrentExtractDate =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.selectedDayModel.occupancy.occupancyChangeForExtractDate(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.currentExtractDate.format('YYYY-MM-DD'),
      );

    if (occupancyChangeForCurrentExtractDate) {
      // Get the serializer for this model
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const serializer = this.store.serializerFor('occupancy');
      // Serialize the attributes
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const serializedOccupancyResponse = serializer.normalize(
        Occupancy,
        occupancyChangeForCurrentExtractDate,
      );
      // Create a model, so we get all the CPs as well
      const occupancyModel = this.store.createRecord(
        'occupancy',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        serializedOccupancyResponse.data.attributes,
      );

      set(this, 'previousTemporaryOccupancy', occupancyModel);

      return occupancyModel;
    }

    return occupancyChangesData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbChangeValue() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.occupancyDataForCurrentExtractDate.occupancyChangeVersusPercentage(
      this.referenceOccupancyChange,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbOutOfSyncUtil() {
    return otbOutOfSync(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      [this.occupancyDataForCurrentExtractDate],
      this.hotelAndMarketService,
      this.intl,
      this.access,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceOccupancyChange() {
    // Subtract the number of refDaysAgo to get the correct extract date
    const extractDate = this.hotelAndMarketService
      .getLocalTime()
      // @ts-expect-error: Incorrect type
      .subtract(this.args.refDaysAgo, 'day')
      .format('YYYY-MM-DD');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.occupancyDataForCurrentExtractDate.occupancyChangeForExtractDate(
      extractDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get roomsBookedChangeValue() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.occupancyDataForCurrentExtractDate.roomsBookedChangeVersus(
      this.referenceOccupancyChange,
    );
  }

  @computed('args.selectedDayModel.mostRecentDailySummary')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get summary() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.args.selectedDayModel?.mostRecentDailySummary;
  }

  <template>
    <PrismCard class="detail-pane-card">
      <:header as |Header|>
        <div class="flex items-center gap-100">
          <Header.title>
            {{t "general.occupancy"}}
          </Header.title>
          {{#if this.isLatestCurrentExtractDate}}
            <span
              class="text-200--regular"
              data-test-id="detail-pane-occupancy-daysago"
              {{! @glint-expect-error: Incorrect type }}
            >{{t "general.daysago" count=@refDaysAgo}}</span>
          {{/if}}

          {{#if
            (or
              this.otbOutOfSyncUtil.isOtbOutOfSync
              this.otbOutOfSyncUtil.isMarketOtbOutOfSync
            )
          }}
            <GeneralIconWithTooltip
              @iconName="sync-disabled"
              @side="top"
              {{! @glint-expect-error: Incorrect type }}
              @tooltipContent={{this.otbOutOfSyncUtil.otbAndMarketOtbUpdatedString}}
            />
          {{/if}}
        </div>
      </:header>
      <:content>
        <div class="flex flex-row">
          <PrismMetricBlock
            @align="left"
            @compact={{true}}
            class="mr-800 w-1/3"
          >
            <:value>
              <span class="flex flex-row items-center">
                <div
                  class="text-400--bold"
                >{{this.occupancyDataForCurrentExtractDate.formattedOccupancyPercentage}}</div>
                {{#if this.isLatestCurrentExtractDate}}
                  <div
                    class="{{changeColorClass this.otbChangeValue}}
                      text-200--regular ml-100"
                  >
                    {{formatOtbValue
                      this.otbChangeValue
                      signDisplay="always"
                      style="percent"
                    }}
                    ({{formatOtbValue
                      this.roomsBookedChangeValue
                      signDisplay="always"
                    }})
                  </div>
                {{/if}}
              </span>
            </:value>
            <:label>{{t "rates.my_OTB"}}</:label>
          </PrismMetricBlock>

          {{#if (hasFeature "otb.market_benchmark")}}
            <PrismMetricBlock @align="left" @compact={{true}}>
              <:value>
                <span class="flex flex-row items-center">
                  <div
                    class="text-400--bold"
                  >{{this.occupancyDataForCurrentExtractDate.formattedBenchmarkPercentage}}</div>
                  {{#if this.isLatestCurrentExtractDate}}
                    <div
                      class="{{changeColorClass this.marketOtbChangeValue}}
                        text-200--regular ml-100"
                    >{{formatOtbValue
                        this.marketOtbChangeValue
                        signDisplay="always"
                        style="percent"
                      }}</div>
                  {{/if}}
                </span>
              </:value>
              <:label>{{t "rates.tooltip.market_otb"}}</:label>
            </PrismMetricBlock>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
